import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { expValEquals } from '@confluence/feature-experiments';

import { toDocumentNode } from '../toDocumentNode.ts';
import { type QueryInteropFetchPolicy } from '../mapFetchPolicyForQuery.ts';

export type QueryCacheBridgeSource = 'apollo' | 'relay';

export type CacheWriteResult =
	| { status: 'written' }
	| { status: 'skipped'; reason: string }
	| { status: 'failed'; error: unknown };

export type QueryCacheBridgeWriteArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	data?: TData | null;
	fetchPolicy?: QueryInteropFetchPolicy;
	useAggClient?: boolean;
};

export type QueryCacheBridgeSyncArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = QueryCacheBridgeWriteArgs<TData, TVariables> & {
	source: QueryCacheBridgeSource;
	error?: unknown;
	skip?: boolean;
};

export type QueryCacheBridgeReadArgs<TVariables extends Record<string, any> = OperationVariables> =
	{
		query: GraphQLTaggedNode;
		variables?: TVariables;
		useAggClient?: boolean;
	};

export type QueryCacheBridgeReadFallbackArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = QueryCacheBridgeReadArgs<TVariables> & {
	source: QueryCacheBridgeSource;
	readOriginal: () => TData | null;
};

function skipped(reason: string): CacheWriteResult {
	return { status: 'skipped', reason };
}

function getQueryCacheBridgeDisabledReason(): string | null {
	if (!expValEquals('confluence_apollo_to_relay_migration', 'isEnabled', true)) {
		return 'apollo-to-relay-migration-disabled';
	}

	if (!expValEquals('confluence_apollo_relay_cache_bridge', 'isEnabled', true)) {
		return 'apollo-relay-cache-bridge-disabled';
	}

	return null;
}

function shouldSkipSyncCache<TData, TVariables extends Record<string, any>>({
	data,
	fetchPolicy,
}: QueryCacheBridgeWriteArgs<TData, TVariables>): string | null {
	if (fetchPolicy === 'no-cache') return 'no-cache';
	if (data == null) return 'data-missing';
	if (typeof window === 'undefined') return 'window-missing';

	return null;
}

function writeSafely(write: () => void): CacheWriteResult {
	try {
		write();
		return { status: 'written' };
	} catch (error) {
		return { status: 'failed', error };
	}
}

function readSafely<TData>(read: () => TData | null | undefined): TData | null {
	try {
		return read() ?? null;
	} catch {
		return null;
	}
}

function getApolloClient(useAggClient?: boolean) {
	if (typeof window === 'undefined') return undefined;

	if (useAggClient) {
		return window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'];
	}

	return window['GLOBAL_APOLLO_CLIENT'];
}

export function readQueryFromApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(args: QueryCacheBridgeReadArgs<TVariables>): TData | null {
	const client = getApolloClient(args.useAggClient);
	if (!client) return null;

	return readSafely(() => {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- cache bridge intentionally checks Apollo during migration fallback reads
		return client.readQuery({
			query: toDocumentNode(args.query),
			variables: args.variables,
		}) as TData;
	});
}

export function readQueryFromRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(args: QueryCacheBridgeReadArgs<TVariables>): TData | null {
	if (typeof window === 'undefined') return null;

	const environment = window['GLOBAL_RELAY_ENV'];
	if (!environment) return null;

	return readSafely(() => {
		const operation = createOperationDescriptor(getRequest(args.query), args.variables ?? {});
		const snapshot = environment.lookup(operation.fragment);

		return snapshot.isMissingData ? null : (snapshot.data as TData);
	});
}

export function writeQueryToApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(args: QueryCacheBridgeWriteArgs<TData, TVariables>): CacheWriteResult {
	const skipReason = shouldSkipSyncCache(args);
	if (skipReason) return skipped(skipReason);

	const client = getApolloClient(args.useAggClient);
	if (!client) return skipped('apollo-client-missing');

	return writeSafely(() => {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- cache bridge intentionally mirrors Relay query results into Apollo during migration
		client.writeQuery({
			query: toDocumentNode(args.query),
			variables: args.variables,
			data: args.data as TData,
		});
	});
}

export function writeQueryToRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(args: QueryCacheBridgeWriteArgs<TData, TVariables>): CacheWriteResult {
	const skipReason = shouldSkipSyncCache(args);
	if (skipReason) return skipped(skipReason);

	const environment = window['GLOBAL_RELAY_ENV'];
	if (!environment) return skipped('relay-environment-missing');

	return writeSafely(() => {
		const operation = createOperationDescriptor(getRequest(args.query), args.variables ?? {});
		environment.commitPayload(operation, args.data as Record<string, unknown>);
	});
}

export function writeQueryToAvailableCaches<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
): {
	apollo: CacheWriteResult;
	relay: CacheWriteResult;
} {
	const disabledReason = getQueryCacheBridgeDisabledReason();
	if (disabledReason) {
		return {
			apollo: skipped(disabledReason),
			relay: skipped(disabledReason),
		};
	}

	return {
		apollo: writeQueryToApollo(args),
		relay: writeQueryToRelay(args),
	};
}

export function writeQueryToSecondaryCache<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
	source: QueryCacheBridgeSource,
): CacheWriteResult {
	return source === 'apollo' ? writeQueryToRelay(args) : writeQueryToApollo(args);
}

export function syncQueryCacheBridge<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	source,
	error,
	skip,
	...writeArgs
}: QueryCacheBridgeSyncArgs<TData, TVariables>): CacheWriteResult {
	if (skip) return skipped('operation-skipped');
	if (error) return skipped('operation-error');

	const disabledReason = getQueryCacheBridgeDisabledReason();
	if (disabledReason) return skipped(disabledReason);

	return writeQueryToSecondaryCache(writeArgs, source);
}

export function readQueryFromSecondaryCache<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(args: QueryCacheBridgeReadArgs<TVariables>, source: QueryCacheBridgeSource): TData | null {
	return source === 'apollo'
		? readQueryFromRelay<TData, TVariables>(args)
		: readQueryFromApollo<TData, TVariables>(args);
}

export function readQueryWithCacheBridgeFallback<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	source,
	readOriginal,
	...readArgs
}: QueryCacheBridgeReadFallbackArgs<TData, TVariables>): TData | null {
	const disabledReason = getQueryCacheBridgeDisabledReason();
	if (disabledReason) {
		return readOriginal();
	}

	try {
		const result = readOriginal();
		return result ?? readQueryFromSecondaryCache<TData, TVariables>(readArgs, source);
	} catch (error) {
		const fallbackResult = readQueryFromSecondaryCache<TData, TVariables>(readArgs, source);

		if (fallbackResult !== null) {
			return fallbackResult;
		}

		throw error;
	}
}

export function readQueryWithFallback<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	args: QueryCacheBridgeReadArgs<TVariables>,
	preferredCache: QueryCacheBridgeSource,
): TData | null {
	const disabledReason = getQueryCacheBridgeDisabledReason();
	if (disabledReason) {
		return preferredCache === 'apollo'
			? readQueryFromApollo<TData, TVariables>(args)
			: readQueryFromRelay<TData, TVariables>(args);
	}

	const primaryResult =
		preferredCache === 'apollo'
			? readQueryFromApollo<TData, TVariables>(args)
			: readQueryFromRelay<TData, TVariables>(args);

	return primaryResult ?? readQueryFromSecondaryCache<TData, TVariables>(args, preferredCache);
}
